/**
 * Data-sources service — the watchlist/sanctions-list catalog (`GET /sources/`).
 *
 * This catalog feeds the data-source picker (the screening filter + the
 * monitoring add-modal). The backend returns the DRF list envelope; each entry
 * is `{ short_name, name, country }`. We map to camelCase at the boundary
 * (ADR-010).
 *
 * The cardinal fact: `shortName` is BOTH our stable id AND the exact token the
 * `/search/` `data_source` query param expects (e.g. "OFAC-SDN", "UK-SANCTIONS").
 * The live app uses `short_name` as the selectable value verbatim
 * (ref-sanctions-dashboard/src/components/single_screening/advance_options/select_data_source.tsx:95-99),
 * so any UI that maps this catalog into selectable items MUST carry `shortName`
 * as the selection id — otherwise the screening `data_source` param is invalid.
 *
 * Verified against the client's production code (same backend); live curl
 * verification deferred while staging is down 2026-06-08 (503) — re-verify the
 * exact `country` values + that page_size=120 covers the whole catalog when it
 * recovers (see findings 2026-06-08).
 *
 * See ADR-010 — Service Layer Designed Fresh.
 */

import { apiEndpoints } from "@/src/config/api/endpoints";

import { api } from "./base_service";
import type { ApiError, Paginated, Result } from "./types";

/** One catalog entry as the backend sends it (snake_case). */
export interface DataSourceRaw {
  short_name: string;
  /** Display name — live `/sources/` returns `null` for some entries. */
  name: string | null;
  /** ISO-3166 alpha-2, or "INT"/"UN" for international bodies; may be absent. */
  country: string | null;
}

/** A sanctions data source / watchlist in the catalog (camelCase boundary). */
export interface DataSource {
  /** Stable id AND the value the `/search/` `data_source` param expects. */
  shortName: string;
  /** Human-readable list name (shown in the picker + chips). */
  name: string;
  /** ISO-3166 alpha-2, "INT"/"UN" for international bodies, or "" when unset. */
  country: string;
}

// Live `/sources/` returns `country: null` for every entry and embeds the ISO
// code in the name as a "<CC> / " prefix (e.g. "AR / MJUS - …", "AU / Australian
// Sanctions List"). When `country` is absent we recover it from that prefix so the
// picker groups by region (instead of dumping everything into "International") and
// drops the now-redundant prefix. Pure fallback: a real `country` from the backend
// always wins, so this becomes a no-op the moment /sources/ returns the field —
// FLAGGED to Hector to populate `country` directly (the clean root-cause fix).
// The `short_name` id is never touched.
const NAME_COUNTRY_PREFIX = /^([A-Z]{2,3}) \/ (.+)$/;

export function adaptDataSource(raw: DataSourceRaw): DataSource {
  // Some live entries have name=null; fall back to the short_name so the picker
  // never renders/searches a null (DataSource.name is typed `string`).
  const rawName = raw.name ?? raw.short_name ?? "";
  let country = raw.country ?? "";
  let name = rawName;
  if (!country) {
    const match = NAME_COUNTRY_PREFIX.exec(rawName);
    if (match) {
      country = match[1];
      name = match[2];
    }
  }
  return { shortName: raw.short_name, name, country };
}

/**
 * `GET /sources/?page_size=120` — the full data-source (watchlist) catalog.
 *
 * The live app fetches a single `page_size=120` page rather than following the
 * `next` cursor (ref data_sources_list_service.ts). We preserve that for now —
 * flagged as a Hector ask if the real catalog ever exceeds 120 entries.
 */
export async function getDataSources(): Promise<Result<DataSource[]>> {
  try {
    const data = await api.get<Paginated<DataSourceRaw>>(apiEndpoints.dataSources().list(), {
      params: { page_size: 120 },
    });
    return { ok: true, value: (data.results ?? []).map(adaptDataSource) };
  } catch (err) {
    // `api` throws a normalized ApiError; surface it so the caller can fall
    // back to the bundled catalog rather than render an empty picker.
    return { ok: false, error: err as ApiError };
  }
}
